import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';

import { cn } from '@april9au/react-common/utils';

import logoUrl from '@/assets/logos/logo.png';

export type NavLink = {
  label: string;
  to: string;
};

export type NavMegaItem = NavLink & { description?: string };

export type NavCategory = {
  label: string;
  items: Array<NavMegaItem>;
};

export type NavEntry = NavLink | NavCategory;

const DEFAULT_ENTRIES: Array<NavEntry> = [
  {
    label: 'About',
    items: [
      {
        label: 'About Us',
        to: '/about',
        description: 'How Meet Real Estate got started in the South Burnett.',
      },
      { label: 'FAQs', to: '/faq', description: 'Common questions about selling, appraisals and our process.' },
    ],
  },
  {
    label: 'Services',
    items: [
      {
        label: 'Selling',
        to: '/selling',
        description: 'An appraisal, a plan, and one agent handling every inspection personally.',
      },
      { label: 'Get an Appraisal', to: '/appraisal', description: 'Request a free, no-obligation property appraisal.' },
      { label: 'Buying', to: '/#services', description: "Buyer's agent services for locals and newcomers to the region." },
      {
        label: 'Property Management',
        to: '/#contact',
        description: 'Property management for owners who want a name and a face.',
      },
    ],
  },
  {
    label: 'Region',
    items: [
      {
        label: 'Postcodes We Cover',
        to: '/postcodes',
        description: 'Suburbs and postcodes across Kingaroy and the South Burnett.',
      },
      {
        label: 'Local Links',
        to: '/local-links',
        description: 'Council contacts, community events and local resources.',
      },
      {
        label: 'Infrastructure & Investment',
        to: '/infrastructure',
        description: 'Major developments and public investment shaping the region.',
      },
      {
        label: 'Economic Growth',
        to: '/economic-growth',
        description: 'Key economic markers for Kingaroy and the South Burnett.',
      },
    ],
  },
  { label: 'Insights', to: '/#insights' },
  { label: 'Contact', to: '/#contact' },
];

function isNavCategory(entry: NavEntry): entry is NavCategory {
  return 'items' in entry;
}

function anchorTargetId(href: string): string | null {
  const hashIndex = href.indexOf('#');
  return hashIndex === -1 ? null : href.slice(hashIndex + 1);
}

function isLinkVisible(link: NavLink, isHome: boolean) {
  const anchorId = anchorTargetId(link.to);
  if (!anchorId) return true;
  // Anchor-only links always work from other routes (they navigate home first),
  // so only check the DOM when we're already on the page the anchor lives on.
  if (!isHome) return true;
  return Boolean(document.getElementById(anchorId));
}

function slugify(label: string) {
  return label.toLowerCase().replace(/\s+/g, '-');
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" d="m2.5 4.5 3.5 3.5 3.5-3.5" />
    </svg>
  );
}

/** Sticky site navigation with a mega-menu. Renders once in the root layout so it's shared across every page. */
export function SiteHeader({ entries = DEFAULT_ENTRIES, className }: { entries?: Array<NavEntry>; className?: string }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [openCategory, setOpenCategory] = useState<string | null>(null);
  const [visibleEntries, setVisibleEntries] = useState(entries);
  const location = useLocation();
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 24);
    }

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const isHome = location.pathname === '/';
    setVisibleEntries(
      entries
        .map((entry) =>
          isNavCategory(entry) ? { ...entry, items: entry.items.filter((item) => isLinkVisible(item, isHome)) } : entry,
        )
        .filter((entry) => (isNavCategory(entry) ? entry.items.length > 0 : isLinkVisible(entry, isHome))),
    );
  }, [entries, location.pathname]);

  useEffect(() => {
    setOpen(false);
    setOpenCategory(null);
  }, [location.pathname]);

  useEffect(() => {
    if (!openCategory) return;

    function onPointerDown(event: MouseEvent) {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setOpenCategory(null);
      }
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpenCategory(null);
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [openCategory]);

  return (
    <header className={cn('nav', scrolled && 'nav--scrolled', className)}>
      <div className="nav__inner">
        <Link to="/" className="nav__logo" onClick={() => setOpen(false)}>
          <img src={logoUrl} alt="Meet Real Estate" />
          <span className="text-brand-name">Meet Real Estate</span>
        </Link>

        <nav ref={navRef} className={cn('nav__links', open && 'nav__links--open')}>
          {visibleEntries.map((entry) => {
            if (isNavCategory(entry)) {
              const id = slugify(entry.label);
              const isOpen = openCategory === entry.label;

              return (
                <div key={entry.label} className={cn('nav__item', isOpen && 'nav__item--open')}>
                  <button
                    type="button"
                    className="nav__link nav__trigger"
                    aria-haspopup="true"
                    aria-expanded={isOpen}
                    aria-controls={`nav-panel-${id}`}
                    onClick={() => setOpenCategory((current) => (current === entry.label ? null : entry.label))}
                  >
                    {entry.label}
                    <ChevronIcon className={cn('nav__chevron', isOpen && 'nav__chevron--open')} />
                  </button>

                  <div id={`nav-panel-${id}`} className="nav__panel" hidden={!isOpen}>
                    <div className="nav__panel-grid">
                      {entry.items.map((item) => (
                        <a
                          key={item.to}
                          href={item.to}
                          className="nav__panel-link"
                          onClick={() => {
                            setOpen(false);
                            setOpenCategory(null);
                          }}
                        >
                          <span className="nav__panel-link-title">{item.label}</span>
                          {item.description ? (
                            <span className="nav__panel-link-description">{item.description}</span>
                          ) : null}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <div key={entry.to} className="nav__item">
                <a href={entry.to} className="nav__link" onClick={() => setOpen(false)}>
                  {entry.label}
                </a>
              </div>
            );
          })}
        </nav>

        <div className="flex items-center gap-6">
          <a href="tel:0493035628" className="btn btn--primary btn--sm hidden md:inline-flex">
            0493 035 628
          </a>

          <button
            type="button"
            className="text-gold md:hidden"
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            onClick={() => setOpen((value) => !value)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="size-6">
              {open ? (
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5M3.75 17.25h16.5" />
              )}
            </svg>
          </button>
        </div>
      </div>
    </header>
  );
}
